import React from 'react';
import { logger } from '../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    logger.error('ErrorBoundary caught an error', {
      error: error.toString(),
      errorInfo,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // In development, you might want to show more details
    if (process.env.NODE_ENV === 'development') {
      logger.error('Error Boundary Caught Error', { error, errorInfo });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReportError = () => {
    // Future: Send error report to support
    const errorReport = {
      error: this.state.error?.toString(),
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    logger.info('User reported error', errorReport);

    // For now, just show a message
    alert('Error report logged. Our team will investigate this issue.');
  };

  render() {
    if (this.state.hasError) {
      // Production error UI
      if (process.env.NODE_ENV === 'production') {
        return (
          <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
              <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>

                  <h2 className="mt-6 text-lg font-medium text-gray-900">
                    Something went wrong
                  </h2>

                  <p className="mt-2 text-sm text-gray-600">
                    We're sorry, but something unexpected happened. Please try refreshing the page.
                  </p>

                  <div className="mt-6 space-y-3">
                    <button
                      onClick={this.handleRetry}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Try Again
                    </button>

                    <button
                      onClick={() => window.location.reload()}
                      className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Refresh Page
                    </button>

                    <button
                      onClick={this.handleReportError}
                      className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Report This Error
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Development error UI with more details
      return (
        <div className="min-h-screen bg-red-50 py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white shadow-lg rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h1 className="text-lg font-medium text-red-800">Development Error</h1>
                  <p className="text-sm text-red-600">This detailed error information is only shown in development mode.</p>
                </div>
              </div>

              {this.state.error && (
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-2">Error Message:</h2>
                  <pre className="bg-red-100 p-4 rounded text-sm text-red-800 overflow-auto">
                    {this.state.error.toString()}
                  </pre>
                </div>
              )}

              {this.state.error?.stack && (
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-2">Stack Trace:</h2>
                  <pre className="bg-gray-100 p-4 rounded text-xs text-gray-800 overflow-auto max-h-64">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}

              {this.state.errorInfo?.componentStack && (
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-2">Component Stack:</h2>
                  <pre className="bg-gray-100 p-4 rounded text-xs text-gray-800 overflow-auto max-h-64">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={this.handleRetry}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;